import { getWatchdutyTileUrl } from 'features/mapping/components/layers/layers.utils';
import { MapClickHandler, MapboxFeature } from 'features/mapping/mapping.types';
import { VisibilityToggledSource } from 'features/mapping/components/layers/VisibilityToggledSource';
import { useMemo } from 'react';
import { addVisible } from 'shared/addVisible';
import { Layer, MapRef, useMapInstance } from 'shared/map-exports';
import { useAuthState } from 'state/useAuthState';
import { useSelectedWeatherStationId } from 'features/weatherStations/hooks/useSelectedWeatherStationId';
import { useWeatherStation } from 'features/weatherStations/hooks/useWeatherStation';
import { LatLng } from 'shared/types';
import { useCenterMap } from 'features/mapping/hooks/useCenterMap';
import { usePulseAnimation } from 'hooks/usePulseAnimation';
import { history } from 'shared/history';
import { useWeatherStationState } from 'state/useWeatherStationState';
import { useTranslation } from 'react-i18next';
import { OVERLAY_ICONS_BEFORE_ID } from 'features/mapping/styles';
import {
  BASE_CIRCLE_RADIUS,
  MAX_ICON_INTERPOLATION_ZOOM_LEVEL,
  MIN_ICON_ZOOM_LEVEL,
  SOURCE_LAYER,
  WEATHER_STATIONS_ARROW_ICONS_LAYER_ID,
  WEATHER_STATIONS_LABELS_LAYER_ID,
  WEATHER_STATIONS_POINT_ICONS_LAYER_ID,
  WEATHER_STATIONS_SELECTED_POINT_ICON_LAYER_ID,
  WEATHER_STATIONS_SOURCE_ID,
} from './WeatherStationsLayer.constants';
import {
  getArrowIconStyle,
  getDynamicLabelStyle,
  getPointIconStyle,
  getSelectedPointIconStyle,
} from './WeatherStationsLayer.styles';

type WeatherStationsLayerProps = {
  visible: boolean;
};

type WeatherStationsFeatureProperties = {
  air_temp?: number;
  elevation: string;
  is_observation_recent: boolean;
  is_offline: boolean;
  is_within_coverage_area: boolean;
  name: string;
  stid: string;
  weather_station_type: 'atmospheric';
  dew_point_temperature?: number;
  relative_humidity?: number;
  wind_direction?: number;
  wind_gust?: number;
  wind_speed?: number;
};

const REFRESH_INTERVAL_SECONDS = 60 * 15; // 15 minutes

const getFeatureCoordinates = (input: {
  mapInstance?: MapRef;
  sourceId: string;
  featureId: number;
}): LatLng | null => {
  const { mapInstance, sourceId, featureId } = input;

  if (!mapInstance) {
    return null;
  }

  const features = mapInstance.querySourceFeatures(sourceId, {
    sourceLayer: SOURCE_LAYER,
  });

  const feature = features.find((f) => f.id === featureId);

  if (feature?.geometry.type === 'Point') {
    const [lng, lat] = feature.geometry.coordinates;
    return { lng, lat };
  }

  return null;
};

export const resolveWeatherStationPath = (
  geoJsonFeatures: MapboxFeature<WeatherStationsFeatureProperties>[],
): string | null => {
  const feature = geoJsonFeatures[0];
  const featureId = feature.id;
  if (featureId === undefined || featureId === null) return null;
  return `/weather_station/${featureId}`;
};

export const weatherStationsClickHandler: MapClickHandler<
  WeatherStationsFeatureProperties
> = (geoJsonFeatures) => {
  const path = resolveWeatherStationPath(geoJsonFeatures);
  if (path) history.push(path);
};

export const WeatherStationsLayer = (
  props: WeatherStationsLayerProps,
): JSX.Element => {
  const { visible } = props;
  const {
    permissions: { isInternalUser },
  } = useAuthState();
  const activeStationId = useSelectedWeatherStationId();
  const { weatherStation } = useWeatherStation({ id: activeStationId });
  const mapInstance = useMapInstance();
  const { focusedSensorType } = useWeatherStationState();
  const { t } = useTranslation();

  usePulseAnimation({
    baseRadius: BASE_CIRCLE_RADIUS,
    isActive: Boolean(activeStationId),
    layerId: WEATHER_STATIONS_SELECTED_POINT_ICON_LAYER_ID,
    map: mapInstance?.getMap(),
    maxZoomLevel: MAX_ICON_INTERPOLATION_ZOOM_LEVEL,
    minZoomLevel: MIN_ICON_ZOOM_LEVEL,
  });

  const activeStationLatLng = useMemo(() => {
    if (!activeStationId) return null;
    const latLngFromFeature = getFeatureCoordinates({
      mapInstance,
      sourceId: WEATHER_STATIONS_SOURCE_ID,
      featureId: activeStationId,
    });
    if (!latLngFromFeature && weatherStation?.location) {
      return weatherStation.location;
    }
    return latLngFromFeature;
  }, [activeStationId, mapInstance, weatherStation?.location]);

  useCenterMap({
    latLng: activeStationLatLng,
    drawerIsOpen: true,
    defaultSnapPointPct: 0.47,
    zoomIn: true,
  });

  return (
    <VisibilityToggledSource
      id={WEATHER_STATIONS_SOURCE_ID}
      visible={visible}
      url={getWatchdutyTileUrl(SOURCE_LAYER)}
      refreshIntervalSeconds={REFRESH_INTERVAL_SECONDS}
    >
      <Layer
        id={WEATHER_STATIONS_SELECTED_POINT_ICON_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(
          getSelectedPointIconStyle(isInternalUser, activeStationId),
          visible,
        )}
      />
      <Layer
        id={WEATHER_STATIONS_ARROW_ICONS_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(getArrowIconStyle(isInternalUser), visible)}
      />
      <Layer
        id={WEATHER_STATIONS_POINT_ICONS_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(
          getPointIconStyle(isInternalUser, focusedSensorType),
          visible,
        )}
      />
      <Layer
        id={WEATHER_STATIONS_LABELS_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        key={focusedSensorType}
        {...addVisible(
          getDynamicLabelStyle(focusedSensorType, isInternalUser, t),
          visible,
        )}
      />
    </VisibilityToggledSource>
  );
};
